// SPDX-License-Identifier: BUSL-1.1

/**
 * Config surface list page.
 *
 */

import { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import { DataTable } from '@/components/data-table';
import { ConfigListSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { ConfigSurface } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Zod schema                                                         */
/* ------------------------------------------------------------------ */

const createSurfaceSchema = z.object({
  key: z
    .string()
    .min(1, 'Key is required')
    .regex(
      /^[a-z0-9]+(-[a-z0-9]+)*$/,
      'Lowercase alphanumeric and hyphens only (e.g. "my-surface")',
    ),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  environments: z.string().optional(),
});

type CreateSurfaceValues = z.infer<typeof createSurfaceSchema>;

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

const columns: ColumnDef<ConfigSurface>[] = [
  {
    accessorKey: 'key',
    header: 'Surface Key',
    cell: ({ row }) => (
      <Link
        to={`/configs/${row.original.key}`}
        className="font-mono text-blue-400 hover:underline"
      >
        {row.original.key}
      </Link>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'environments',
    header: 'Environments',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex gap-1">
        {row.original.environments.map((env) => (
          <span
            key={env}
            className="rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-400"
          >
            {env}
          </span>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => {
      const date = new Date(row.original.createdAt);
      return (
        <span className="text-neutral-400">
          {date.toLocaleDateString()}
        </span>
      );
    },
  },
];

/* ------------------------------------------------------------------ */
/*  Register Surface Dialog                                            */
/* ------------------------------------------------------------------ */

function RegisterSurfaceDialog() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateSurfaceValues>({
    resolver: zodResolver(createSurfaceSchema),
    defaultValues: {
      key: '',
      name: '',
      description: '',
      environments: 'production,staging',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: CreateSurfaceValues) => {
      const body = {
        ...values,
        environments: values.environments
          ? values.environments.split(',').map((e) => e.trim()).filter(Boolean)
          : ['production', 'staging'],
      };
      return apiFetch('/configs', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: (_data, variables) => {
      showSuccess(`Surface "${variables.key}" created`);
      queryClient.invalidateQueries({ queryKey: ['configs'] });
      form.reset();
      setOpen(false);
    },
    onError: (err) => showApiError(err),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="secondary" />}>
        Register Surface
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Register Config Surface</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Surface Key</FormLabel>
                  <FormControl>
                    <Input placeholder="my-surface" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My Surface" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="environments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environments</FormLabel>
                  <FormControl>
                    <Input placeholder="production,staging" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="mt-2"
            >
              {mutation.isPending ? 'Creating...' : 'Create Surface'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function ConfigListPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['configs'],
    queryFn: () => apiFetch<ConfigSurface[]>('/configs'),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-100">
          Config Surfaces
        </h1>
        <RegisterSurfaceDialog />
      </div>

      {isLoading && <ConfigListSkeleton />}

      {data && (
        <DataTable
          columns={columns}
          data={data}
          emptyMessage="No config surfaces registered yet."
        />
      )}
    </div>
  );
}
